/**
 * What the wall shows, per window (Part 09 decision 4): the current
 * project, Everything (`all`, the user's active projects) or Archived. It
 * outlives the wall's remount on a project switch: Everything stays,
 * Archived belongs to the project it was chosen in.
 *
 * Everything's cards from other projects live here, never in the frames
 * store (which holds the current project only): read-only views, refreshed
 * by `use-wall-scope-feed.ts`, patched by the actions that write through
 * their own project (Pin/Unpin, Archive), and dropped when another scope
 * is chosen (Everything then starts again at the top).
 */
import { create } from "zustand";
import type { FrameData } from "@/lib/frame-data";
import { useFramesStore } from "@/stores/frames-store";

export type WallScope =
  | { kind: "project" }
  | { kind: "all" }
  | { kind: "archived" };

export type ForeignFramePatch = Partial<
  Pick<FrameData, "archivedAt" | "metadata">
>;

interface WallScopeState {
  scope: WallScope;
  /** The project Archived was chosen in. */
  scopeProjectId: string | null;
  /** Other projects' frames of Everything, by id. */
  foreignFramesById: Readonly<Record<string, FrameData>>;
  setScope: (scope: WallScope, projectId: string) => void;
  /** Replace the map (`reset`) or add to it, by id. */
  putForeignFrames: (frames: readonly FrameData[], reset: boolean) => void;
  patchForeignFrame: (frameId: string, patch: ForeignFramePatch) => void;
}

const PROJECT: WallScope = { kind: "project" };
const NO_FRAMES: Readonly<Record<string, FrameData>> = {};

export const useWallScopeStore = create<WallScopeState>((set) => ({
  scope: PROJECT,
  scopeProjectId: null,
  foreignFramesById: NO_FRAMES,
  setScope: (scope, projectId) =>
    set(
      scope.kind === "all"
        ? { scope, scopeProjectId: projectId }
        : { scope, scopeProjectId: projectId, foreignFramesById: NO_FRAMES },
    ),
  putForeignFrames: (frames, reset) =>
    set((state) => {
      if (frames.length === 0) {
        return reset ? { foreignFramesById: NO_FRAMES } : state;
      }
      const next = reset ? {} : { ...state.foreignFramesById };
      for (const frame of frames) next[frame.frameId] = frame;
      return { foreignFramesById: next };
    }),
  patchForeignFrame: (frameId, patch) =>
    set((state) => {
      const frame = state.foreignFramesById[frameId];
      if (!frame) return state;
      return {
        foreignFramesById: {
          ...state.foreignFramesById,
          [frameId]: { ...frame, ...patch },
        },
      };
    }),
}));

/** The scope shown in `projectId`: Archived of another project reads as the project. */
export function useWallScope(projectId: string): WallScope {
  return useWallScopeStore((state) =>
    state.scope.kind === "archived" && state.scopeProjectId !== projectId
      ? PROJECT
      : state.scope,
  );
}

/**
 * The card of another project that Everything shows, or undefined: the one
 * predicate for a foreign card. The frames store holds the current project
 * only, so a frame it has is never foreign.
 */
export function foreignWallFrame(frameId: string): FrameData | undefined {
  if (useFramesStore.getState().framesById[frameId]) return undefined;
  return useWallScopeStore.getState().foreignFramesById[frameId];
}
